import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import {
  lookupCustomerName,
  usersDataSource,
} from "../requests/usersDataSource";
import type {
  ScopeCustomer,
  User,
  UserAssignmentDraft,
} from "../types";

interface Props {
  drafts: UserAssignmentDraft[];
  onChange: (next: UserAssignmentDraft[]) => void;
}

const composeKey = (customerId: number, auth0Id: string) =>
  `${customerId}::${auth0Id}`;

const TagUsersSection: React.FC<Props> = ({ drafts, onChange }) => {
  const api = useAxiosAuth0();
  const tenantType = useAppSelector((s) => s.userProfile.TenantType);
  const profileTenantId = useAppSelector((s) => s.userProfile.tenantId);
  const partnerId = useAppSelector((s) => s.selection.partnerId);
  const tenantId = useAppSelector((s) => s.selection.tenantId);
  const tenantName = useAppSelector((s) => s.selection.tenantName);

  /** Empty until profile loads — avoids wrong CUSTOMER vs PARTNER assumptions on first paint. */
  const tenantTypeLoaded = tenantType !== "";

  /** Same fallback as Notifications V2 / SideNav when `partnerId` is not yet in selection. */
  const effectivePartnerId =
    partnerId || (tenantType === "PARTNER" ? profileTenantId : 0);

  // Tenant-context derivation (read-only against Redux):
  // - CUSTOMER  → user is locked to their tenant; no picker.
  // - PARTNER / GLOBAL_ADMIN viewing all customers (tenantId === partnerId)
  //   → in-step customer picker is required; default empty until chosen.
  // - PARTNER / GLOBAL_ADMIN viewing a specific customer
  //   → default to that customer; picker still available so the user can
  //     pull users from a different customer into the same tag.
  const isCustomerView = tenantType === "CUSTOMER";
  const isPartnerView = !isCustomerView && tenantId === partnerId;

  const [customers, setCustomers] = useState<ScopeCustomer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    () => {
      if (isCustomerView) return tenantId;
      if (!isPartnerView && tenantId) return tenantId;
      return null;
    }
  );

  // Resolve a friendly customer name for the active selection. We prefer
  // the SideNav's tenantName when it matches, then the customers list from
  // GET /tenant/customers, then a synthetic label.
  const selectedCustomerName = useMemo<string | null>(() => {
    if (selectedCustomerId == null) return null;
    if (selectedCustomerId === tenantId && tenantName) return tenantName;
    const fromList = customers.find((c) => c.id === selectedCustomerId);
    if (fromList) return fromList.name;
    return lookupCustomerName(selectedCustomerId) ?? `Customer ${selectedCustomerId}`;
  }, [selectedCustomerId, customers, tenantId, tenantName]);

  /** CUSTOMER tenants: sync picker tenant once `TenantType` is known (same bootstrap as Notifications V2). */
  useEffect(() => {
    if (!tenantTypeLoaded) return;
    if (!isCustomerView || !tenantId) return;
    setSelectedCustomerId((prev) => (prev !== tenantId ? tenantId : prev));
  }, [tenantTypeLoaded, isCustomerView, tenantId]);

  // Load customers in scope (only needed for the picker dropdown).
  useEffect(() => {
    if (isCustomerView) return;
    let cancelled = false;
    setCustomersLoading(true);
    usersDataSource
      .listCustomersInScope(api, effectivePartnerId || null)
      .then((list) => {
        if (cancelled) return;
        setCustomers(list);
        // If we don't have a selection yet, pick the SideNav tenant if
        // it appears in the list (i.e. user came from a customer view).
        if (selectedCustomerId == null && tenantId && list.some((c) => c.id === tenantId)) {
          setSelectedCustomerId(tenantId);
        }
      })
      .finally(() => {
        if (!cancelled) setCustomersLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Intentionally only depends on scope inputs; selectedCustomerId is
    // initialized inside on first load and shouldn't retrigger this fetch.
  }, [api, effectivePartnerId, isCustomerView]);

  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [pickedIds, setPickedIds] = useState<Set<string>>(new Set());

  // Load users for the selected customer.
  useEffect(() => {
    if (!selectedCustomerId) {
      setUsers([]);
      return;
    }
    let cancelled = false;
    setUsersLoading(true);
    setUsersError(null);
    setPickedIds(new Set());
    usersDataSource
      .listUsersForCustomer(api, selectedCustomerId)
      .then((list) => {
        if (cancelled) return;
        setUsers(list);
      })
      .catch(() => {
        if (cancelled) return;
        setUsers([]);
        setUsersError("Couldn't load users for this customer.");
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, selectedCustomerId]);

  const draftKeys = useMemo(
    () => new Set(drafts.map((d) => composeKey(d.customerId, d.auth0Id))),
    [drafts]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.email, u.displayName ?? ""]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [users, search]);

  const togglePicked = useCallback((auth0Id: string) => {
    setPickedIds((prev) => {
      const next = new Set(prev);
      if (next.has(auth0Id)) next.delete(auth0Id);
      else next.add(auth0Id);
      return next;
    });
  }, []);

  const handleAttachSelection = useCallback(() => {
    if (pickedIds.size === 0 || !selectedCustomerId) return;
    const customerName = selectedCustomerName ?? `Customer ${selectedCustomerId}`;
    const additions: UserAssignmentDraft[] = Array.from(pickedIds)
      .filter(
        (auth0Id) =>
          !draftKeys.has(composeKey(selectedCustomerId, auth0Id))
      )
      .map((auth0Id) => {
        const u = users.find((x) => x.auth0Id === auth0Id);
        return {
          auth0Id,
          email: u?.email ?? auth0Id,
          displayName: u?.displayName ?? null,
          customerId: selectedCustomerId,
          customerName,
        };
      });
    if (additions.length === 0) return;
    onChange([...drafts, ...additions]);
    setPickedIds(new Set());
  }, [
    drafts,
    draftKeys,
    pickedIds,
    selectedCustomerId,
    selectedCustomerName,
    users,
    onChange,
  ]);

  const handleRemove = useCallback(
    (key: string) => {
      onChange(
        drafts.filter(
          (d) => composeKey(d.customerId, d.auth0Id) !== key
        )
      );
    },
    [drafts, onChange]
  );

  // Group drafts by customer for the tabs UI.
  const draftsByCustomer = useMemo(() => {
    const grouped = new Map<number, UserAssignmentDraft[]>();
    for (const d of drafts) {
      const arr = grouped.get(d.customerId) ?? [];
      arr.push(d);
      grouped.set(d.customerId, arr);
    }
    return Array.from(grouped.entries());
  }, [drafts]);

  const distinctCustomerCount = draftsByCustomer.length;

  const [activeDraftTab, setActiveDraftTab] = useState<number | null>(
    draftsByCustomer[0]?.[0] ?? null
  );

  useEffect(() => {
    if (draftsByCustomer.length === 0) {
      if (activeDraftTab !== null) setActiveDraftTab(null);
      return;
    }
    const stillExists = draftsByCustomer.find(
      ([cid]) => cid === activeDraftTab
    );
    if (!stillExists) {
      setActiveDraftTab(draftsByCustomer[0][0]);
    }
  }, [draftsByCustomer, activeDraftTab]);

  const handleRemoveCustomerTab = useCallback(
    (customerId: number) => {
      onChange(drafts.filter((d) => d.customerId !== customerId));
    },
    [drafts, onChange]
  );

  /** Click on a tab → sync the picker below to that customer. */
  const handleTabClick = useCallback((customerId: number) => {
    setActiveDraftTab(customerId);
    setSelectedCustomerId(customerId);
  }, []);

  const activeTabItems = useMemo(() => {
    if (activeDraftTab == null) return [];
    return drafts.filter((d) => d.customerId === activeDraftTab);
  }, [drafts, activeDraftTab]);

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-md font-semibold text-semantic-text-primary">
          Users
        </h3>
        <p className="text-xs text-semantic-text-secondary mt-1 md:max-w-2xl">
          Grant tag-based visibility to users. In <strong>Add users</strong>,
          choose a customer and pick which of its users should see resources
          attached to this tag. You can mix users from multiple customers in the
          same tag.
        </p>
      </div>

      {/* --- Selected users (tabs by customer, capped height) --- */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-semantic-text-primary">
            Selected users
          </h4>
          <span className="text-xs text-semantic-text-secondary">
            {drafts.length} user{drafts.length === 1 ? "" : "s"} ·{" "}
            {distinctCustomerCount} customer
            {distinctCustomerCount === 1 ? "" : "s"}
          </span>
        </div>
        {drafts.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-semantic-border-secondary rounded-md">
            <p className="text-sm text-semantic-text-secondary">
              No users selected yet. Use <strong>Add users</strong> below to
              choose a customer and attach users.
            </p>
          </div>
        ) : (
          <div className="border border-semantic-border-secondary rounded-md overflow-hidden bg-semantic-bg-primary">
            {/* Tabs row */}
            <div className="flex items-stretch overflow-x-auto custom-scrollbar bg-semantic-bg-secondary border-b border-semantic-border-secondary">
              {draftsByCustomer.map(([customerId, items]) => {
                const label =
                  items[0]?.customerName ?? `Customer ${customerId}`;
                const isActive = customerId === activeDraftTab;
                return (
                  <div
                    key={customerId}
                    className={`flex items-center gap-1.5 border-r border-semantic-border-secondary ${
                      isActive
                        ? "bg-semantic-bg-primary border-b-2 border-b-semantic-border-brand-secondary -mb-px"
                        : "hover:bg-semantic-state-active"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleTabClick(customerId)}
                      className={`px-3 py-2 text-sm font-medium ${
                        isActive
                          ? "text-semantic-text-brand"
                          : "text-semantic-text-primary"
                      }`}
                    >
                      {label}
                      <span className="ml-1.5 text-xs text-semantic-text-secondary">
                        ({items.length})
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveCustomerTab(customerId);
                      }}
                      aria-label={`Remove all users from ${label}`}
                      title={`Remove all users from ${label}`}
                      className="mr-2 w-5 h-5 flex items-center justify-center rounded text-semantic-text-secondary hover:text-semantic-text-error hover:bg-semantic-state-active text-sm leading-none"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Active-tab items list */}
            <div className="max-h-[200px] overflow-y-auto custom-scrollbar divide-y divide-semantic-border-secondary">
              {activeTabItems.length === 0 ? (
                <div className="px-3 py-6 text-sm text-semantic-text-secondary text-center">
                  No users in this customer.
                </div>
              ) : (
                activeTabItems.map((d) => {
                  const key = composeKey(d.customerId, d.auth0Id);
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between px-3 py-2 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <span className="text-semantic-text-primary truncate block">
                          {d.displayName || d.email}
                        </span>
                        <span className="text-xs text-semantic-text-secondary font-mono truncate block">
                          {d.email}
                        </span>
                      </div>
                      <GenericButton
                        label="Remove"
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRemove(key)}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* --- Add users picker (customer row matches NotificationsV2 GroupMembersSection) --- */}
      <div className="border border-semantic-border-primary rounded-md p-4 bg-semantic-bg-secondary space-y-4">
        <h4 className="text-sm font-semibold text-semantic-text-primary">
          {drafts.length === 0 ? "Add users" : "Add more users"}
        </h4>

        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-semantic-text-secondary">
              Customer:
            </span>
            {isCustomerView ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-semantic-bg-brand-secondary text-semantic-text-brand whitespace-nowrap">
                {selectedCustomerName ?? "—"}
              </span>
            ) : (
              <select
                value={selectedCustomerId ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedCustomerId(v ? Number(v) : null);
                }}
                disabled={customersLoading}
                className="px-2 py-1 text-xs font-medium rounded-sm bg-semantic-bg-brand-secondary text-semantic-text-brand border border-semantic-border-brand-secondary cursor-pointer hover:bg-semantic-state-active focus:outline-none focus:ring-2 focus:ring-semantic-border-brand-secondary"
                aria-label="Active customer"
              >
                <option value="" disabled>
                  {customersLoading ? "Loading…" : "Select a customer"}
                </option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <span className="text-xs text-semantic-text-secondary md:text-right">
            Pick users from <strong>{selectedCustomerName ?? "—"}</strong>
          </span>
        </div>

        {selectedCustomerId == null ? (
          <div className="text-center py-8 border border-dashed border-semantic-border-secondary rounded-md bg-semantic-bg-primary">
            <p className="text-sm text-semantic-text-secondary">
              Select a customer to load its users.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <GenericInput
                label="Filter users"
                value={search}
                onChange={setSearch}
                placeholder="Search by name or email…"
                type="search"
              />
              <GenericButton
                label={
                  pickedIds.size === 0
                    ? "Add"
                    : `Add ${pickedIds.size} user${
                        pickedIds.size === 1 ? "" : "s"
                      }`
                }
                variant="brand"
                size="md"
                disabled={pickedIds.size === 0}
                onClick={handleAttachSelection}
              />
            </div>

            <div className="mt-3 border border-semantic-border-secondary rounded-md overflow-hidden bg-semantic-bg-primary">
              <div className="grid grid-cols-[36px_1fr_1fr] gap-3 px-3 py-2 text-xs font-semibold text-semantic-text-secondary uppercase tracking-wide bg-semantic-bg-secondary border-b border-semantic-border-secondary">
                <span></span>
                <span>Name</span>
                <span>Email</span>
              </div>
              <div className="max-h-[220px] overflow-y-auto custom-scrollbar divide-y divide-semantic-border-secondary">
                {usersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-semantic-bg-brand-bold-secondary" />
                  </div>
                ) : usersError ? (
                  <div className="px-3 py-6 text-sm text-semantic-text-danger">
                    {usersError}
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="px-3 py-8 text-sm text-semantic-text-secondary text-center">
                    {users.length === 0
                      ? "No users for this customer."
                      : "No matches for the current filter."}
                  </div>
                ) : (
                  filteredUsers.map((u) => {
                    const key = composeKey(selectedCustomerId, u.auth0Id);
                    const isAttached = draftKeys.has(key);
                    const isPicked = pickedIds.has(u.auth0Id);
                    return (
                      <button
                        key={u.auth0Id}
                        type="button"
                        onClick={() => !isAttached && togglePicked(u.auth0Id)}
                        disabled={isAttached}
                        className={`w-full grid grid-cols-[36px_1fr_1fr] gap-3 px-3 py-2 text-sm text-left items-center transition-colors ${
                          isAttached
                            ? "opacity-50 cursor-not-allowed"
                            : isPicked
                            ? "bg-semantic-state-active"
                            : "hover:bg-semantic-state-active"
                        }`}
                      >
                        <span className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isPicked || isAttached}
                            readOnly
                            disabled={isAttached}
                            className="h-4 w-4 cursor-pointer accent-semantic-bg-brand-bold-secondary"
                          />
                        </span>
                        <span className="text-semantic-text-primary truncate">
                          {u.displayName || u.email}
                          {isAttached && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-semantic-bg-secondary text-semantic-text-secondary">
                              Already added
                            </span>
                          )}
                        </span>
                        <span className="text-semantic-text-secondary truncate">
                          {u.email}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TagUsersSection;
